// Browser-flow adapter reused by the adoption candidate. Credentials stay in HttpOnly cookies owned by the gateway; this module
// accepts or returns no session key and never invokes native login/logout.
import { parseManagedSessions, type ManagedSessionSnapshot } from "./cw-managed-sessions";
import { browserCredentialOrder, CredentialResponseUncertain } from "./cw-credential-order";

export interface LibraryProfile extends Record<string, unknown> {
  id: string | number;
}

export type BrowserMfaMethod = "totp" | "recovery_code";

export type LibrarySessionResult =
  | { kind: "authenticated"; profile: LibraryProfile }
  | { kind: "sessions"; snapshot: ManagedSessionSnapshot }
  | { kind: "unsupported" }
  | { kind: "management-unavailable" }
  | { kind: "mfa-required"; revision: number; methods: readonly BrowserMfaMethod[] }
  | { kind: "ended" }
  | { kind: "unconfirmed"; status: number }
  | { kind: "transition-pending" }
  | { kind: "superseded" };

export class LibrarySessionProtocolError extends Error {
  constructor() {
    super("Session response could not be verified.");
  }
}

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validId(value: unknown): value is string | number {
  return (
    (typeof value === "number" && Number.isFinite(value) && value > 0) ||
    (typeof value === "string" && value.trim().length > 0)
  );
}

async function json(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new LibrarySessionProtocolError();
  }
}

// Stock browser MFA carries its pending login in HttpOnly cookies, not in a
// native challenge-token envelope. Return only a local context identifier.
function pendingMfaMethods(value: unknown): BrowserMfaMethod[] | null {
  if (
    !object(value) ||
    value.status !== 401 ||
    !object(value.meta) ||
    value.meta.is_authenticated !== false ||
    !object(value.data) ||
    "user" in value.data ||
    !Array.isArray(value.data.flows)
  )
    return null;
  const pending = value.data.flows.filter((flow) => object(flow) && flow.is_pending === true);
  if (pending.length !== 1 || !object(pending[0]) || pending[0].id !== "mfa_authenticate")
    return null;
  const types: unknown = pending[0].types;
  if (!Array.isArray(types) || !types.every((type) => typeof type === "string")) return null;
  const methods: BrowserMfaMethod[] = [];
  if (types.includes("totp")) methods.push("totp");
  if (types.includes("recovery_codes")) methods.push("recovery_code");
  return methods.length ? methods : null;
}

export const BROWSER_SESSION_BOUNDARY = "caltana:session-changed";
const BROWSER_SESSION_STORAGE_BOUNDARY = "caltana:session-boundary";

function notifyBoundary(broadcast = true): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(BROWSER_SESSION_BOUNDARY));
  if (!broadcast) return;
  try {
    // No identity or credential is stored. Existing WL lifecycle uses the same
    // credential-free signal so either application can invalidate private UI.
    localStorage.setItem(
      BROWSER_SESSION_STORAGE_BOUNDARY,
      String(Date.now()) + ":" + Math.random(),
    );
    localStorage.removeItem(BROWSER_SESSION_STORAGE_BOUNDARY);
  } catch {
    /* Storage policy cannot prevent the local revision barrier. */
  }
}

export class LibraryBrowserSession {
  private revision = 0;
  private pendingRevision: number | null = null;
  private mfaPendingRevision: number | null = null;

  constructor(private readonly fetcher: typeof fetch = fetch) {}

  // The eventual UI must call this on known external/cross-tab transitions.
  // This local clock does not claim cross-tab or delayed Set-Cookie isolation.
  get currentRevision(): number {
    return this.revision;
  }
  get transitionPending(): boolean {
    return this.pendingRevision !== null;
  }

  invalidate(broadcast = true): void {
    this.revision += 1;
    this.pendingRevision = null;
    this.mfaPendingRevision = null;
    notifyBoundary(broadcast);
  }

  private transition(
    operation: (revision: number) => Promise<LibrarySessionResult>,
  ): Promise<LibrarySessionResult> {
    this.mfaPendingRevision = null;
    const revision = ++this.revision;
    this.pendingRevision = revision;
    notifyBoundary(false);
    return this.runAt(revision, () => this.orderedAt(revision, () => operation(revision))).finally(
      () => {
        // An older A completion must not release the barrier for pending B.
        if (this.pendingRevision === revision) {
          this.pendingRevision = null;
          notifyBoundary();
        }
      },
    );
  }

  private orderedAt(
    revision: number,
    operation: () => Promise<LibrarySessionResult>,
  ): Promise<LibrarySessionResult> {
    return browserCredentialOrder.run<LibrarySessionResult>(async () => {
      if (revision !== this.revision) return { kind: "superseded" };
      // Readers do not acquire the writer lock. Invalidate sibling private UI
      // BEFORE this granted writer can change shared HttpOnly cookies.
      notifyBoundary();
      try {
        return revision === this.revision ? await operation() : { kind: "superseded" };
      } finally {
        // Even an externally superseded writer may already have changed cookies.
        // Its cleared pendingRevision must not suppress the completion signal.
        notifyBoundary();
      }
    });
  }

  private async runAt(
    revision: number,
    operation: () => Promise<LibrarySessionResult>,
  ): Promise<LibrarySessionResult> {
    try {
      const result = await operation();
      return revision === this.revision ? result : { kind: "superseded" };
    } catch (error) {
      if (revision !== this.revision) return { kind: "superseded" };
      throw error;
    }
  }

  private async request(path: string, init?: RequestInit, cookieInert = false): Promise<Response> {
    try {
      const response = await this.fetcher.call(
        globalThis,
        path.startsWith("/") ? path : "/api/cw/" + path,
        {
          ...init,
          credentials: "include",
          cache: "no-store",
          redirect: "error",
        },
      );
      // Headers alone are not a completed credential response. Drain before a
      // participating writer can release its lock, including superseded UI.
      await response.clone().arrayBuffer();
      return response;
    } catch {
      // The consumer-profile gateway never forwards cookie mutations, including
      // errors. A failed proof cannot strand the credential uncertainty marker.
      if (cookieInert) throw new LibrarySessionProtocolError();
      throw new CredentialResponseUncertain();
    }
  }

  private async csrf(signal?: AbortSignal): Promise<string> {
    const response = await this.request("csrf", { method: "GET", ...(signal ? { signal } : {}) });
    if (response.status !== 200) throw new LibrarySessionProtocolError();
    const value = await json(response);
    if (!object(value) || typeof value.csrf_token !== "string" || !value.csrf_token.trim()) {
      throw new LibrarySessionProtocolError();
    }
    return value.csrf_token;
  }

  private async profileAt(revision: number, signal?: AbortSignal): Promise<LibrarySessionResult> {
    const response = await this.request("profile", {
      method: "GET",
      ...(signal ? { signal } : {}),
    });
    if (revision !== this.revision) return { kind: "superseded" };
    if (response.status !== 200) return { kind: "unconfirmed", status: response.status };
    const value = await json(response);
    if (revision !== this.revision) return { kind: "superseded" };
    if (!object(value) || !validId(value.id)) throw new LibrarySessionProtocolError();
    return { kind: "authenticated", profile: value as LibraryProfile };
  }

  currentProfile(): Promise<LibrarySessionResult> {
    // A read started during login/logout could otherwise share its revision
    // and later publish the pre-transition identity. No request or identity is
    // produced while the current intentional transition is unresolved.
    if (this.pendingRevision !== null) return Promise.resolve({ kind: "transition-pending" });
    const revision = this.revision;
    return this.runAt(revision, () => this.profileAt(revision));
  }

  signIn(email: string, password: string): Promise<LibrarySessionResult> {
    return this.transition((revision) => this.signInAt(revision, email, password));
  }

  private async signInAt(
    revision: number,
    email: string,
    password: string,
  ): Promise<LibrarySessionResult> {
    const csrf = await this.csrf();
    if (revision !== this.revision) return { kind: "superseded" };
    const response = await this.request("auth/login", {
      method: "POST",
      headers: { "content-type": "application/json", "x-csrftoken": csrf },
      body: JSON.stringify({ email, password }),
    });
    if (revision !== this.revision) return { kind: "superseded" };
    if (response.status === 401) {
      let value: unknown;
      try {
        value = await response.json();
      } catch {
        /* Invalid pending envelopes are denied. */
      }
      if (revision !== this.revision) return { kind: "superseded" };
      const methods = pendingMfaMethods(value);
      if (methods) {
        this.mfaPendingRevision = revision;
        return { kind: "mfa-required", revision, methods };
      }
    }
    if (response.status !== 200) return { kind: "unconfirmed", status: response.status };
    return this.completeSignInAt(revision, response);
  }

  private async completeSignInAt(
    revision: number,
    response: Response,
    signal?: AbortSignal,
    requireMfa = false,
  ): Promise<LibrarySessionResult> {
    const value = await json(response);
    if (revision !== this.revision || signal?.aborted) return { kind: "superseded" };
    if (
      !object(value) ||
      value.status !== 200 ||
      !object(value.meta) ||
      value.meta.is_authenticated !== true ||
      !object(value.data) ||
      !object(value.data.user) ||
      !validId(value.data.user.id)
    )
      throw new LibrarySessionProtocolError();
    if (
      requireMfa &&
      (!Array.isArray(value.data.methods) ||
        !value.data.methods.some((method) => object(method) && method.method === "mfa"))
    ) {
      throw new LibrarySessionProtocolError();
    }
    const loginId = value.data.user.id;
    // Both password-only and factor completion rotate CSRF. Identity still
    // comes from the current selected credential's flat-profile proof.
    await this.csrf(signal);
    if (revision !== this.revision || signal?.aborted) return { kind: "superseded" };
    const profile = await this.profileAt(revision, signal);
    if (profile.kind === "authenticated" && String(profile.profile.id) !== String(loginId)) {
      throw new LibrarySessionProtocolError();
    }
    return profile;
  }

  verifyMfa(code: string, revision: number, signal?: AbortSignal): Promise<LibrarySessionResult> {
    // Never start a new transition: an old pending A must not become current
    // again merely because its verification UI is submitted after B signs in.
    if (revision !== this.revision || this.mfaPendingRevision !== revision || signal?.aborted) {
      return Promise.resolve({ kind: "superseded" });
    }
    if (this.pendingRevision !== null) return Promise.resolve({ kind: "transition-pending" });
    if (typeof code !== "string" || !code.trim())
      return Promise.resolve({ kind: "unconfirmed", status: 400 });
    this.pendingRevision = revision;
    notifyBoundary(false);
    return this.runAt(revision, () =>
      this.orderedAt(revision, () =>
        !signal?.aborted
          ? this.verifyMfaAt(code, revision, signal)
          : Promise.resolve<LibrarySessionResult>({ kind: "superseded" }),
      ),
    ).finally(() => {
      if (this.pendingRevision === revision) {
        this.pendingRevision = null;
        notifyBoundary();
      }
    });
  }

  private async verifyMfaAt(
    code: string,
    revision: number,
    signal?: AbortSignal,
  ): Promise<LibrarySessionResult> {
    const csrf = await this.csrf(signal);
    if (revision !== this.revision || this.mfaPendingRevision !== revision || signal?.aborted) {
      return { kind: "superseded" };
    }
    const response = await this.request("auth/2fa/authenticate", {
      method: "POST",
      headers: { "content-type": "application/json", "x-csrftoken": csrf },
      body: JSON.stringify({ code }),
      ...(signal ? { signal } : {}),
    });
    if (revision !== this.revision || signal?.aborted) return { kind: "superseded" };
    if (response.status !== 200) {
      if (![400, 429].includes(response.status) && response.status < 500) {
        this.mfaPendingRevision = null;
      }
      return { kind: "unconfirmed", status: response.status };
    }
    this.mfaPendingRevision = null;
    return this.completeSignInAt(revision, response, signal, true);
  }

  cancelMfa(revision: number): void {
    // Local cancellation does not terminate any server session or clear cookies.
    // An old A cancel cannot invalidate B's newer local state.
    if (revision === this.revision && this.mfaPendingRevision === revision) this.invalidate();
  }

  private async managedProfileAt(revision: number): Promise<LibrarySessionResult> {
    const response = await this.request("/api/cw-consumer/profile", { method: "GET" }, true);
    if (revision !== this.revision) return { kind: "superseded" };
    if (response.status === 401) return { kind: "ended" };
    if (response.status !== 200) return { kind: "unconfirmed", status: response.status };
    const value = await json(response);
    if (revision !== this.revision) return { kind: "superseded" };
    if (!object(value) || !validId(value.id)) throw new LibrarySessionProtocolError();
    const channel = response.headers.get("x-caltana-auth-channel");
    if (channel === "legacy") return { kind: "unsupported" };
    if (channel !== "library") throw new LibrarySessionProtocolError();
    return { kind: "authenticated", profile: value as LibraryProfile };
  }

  async listManagedSessions(): Promise<LibrarySessionResult> {
    if (this.pendingRevision !== null) return { kind: "transition-pending" };
    const revision = this.revision;
    return this.runAt(revision, () =>
      browserCredentialOrder.run(async () => {
        if (revision !== this.revision || this.pendingRevision !== null)
          return { kind: "superseded" };
        const proof = await this.managedProfileAt(revision);
        if (proof.kind !== "authenticated") return proof;
        const response = await this.request("auth/sessions", { method: "GET" });
        if (revision !== this.revision) return { kind: "superseded" };
        if (response.status === 401) return { kind: "ended" };
        if (response.status === 409) return { kind: "management-unavailable" };
        if (response.status !== 200) return { kind: "unconfirmed", status: response.status };
        const sessions = parseManagedSessions(await json(response));
        const settledProof = await this.managedProfileAt(revision);
        if (settledProof.kind !== "authenticated") return settledProof;
        if (settledProof.profile.id !== proof.profile.id) return { kind: "superseded" };
        return { kind: "sessions", snapshot: { actorId: proof.profile.id, revision, sessions } };
      }),
    );
  }

  revokeManagedSessions(
    snapshot: ManagedSessionSnapshot,
    ids: readonly number[],
  ): Promise<LibrarySessionResult> {
    if (snapshot.revision !== this.revision || this.pendingRevision !== null) {
      return Promise.resolve({ kind: "superseded" });
    }
    const selected = [...new Set(ids)];
    if (
      !selected.length ||
      selected.some((id) => !snapshot.sessions.some((row) => row.id === id))
    ) {
      return Promise.reject(new LibrarySessionProtocolError());
    }
    // Reuse the existing credential writer lock, uncertainty marker and
    // pre-dispatch/settled sibling boundaries; never fall back to native logout.
    return this.transition(async (revision) => {
      const proof = await this.managedProfileAt(revision);
      if (proof.kind !== "authenticated") return proof;
      if (proof.profile.id !== snapshot.actorId) return { kind: "superseded" };
      const csrf = await this.csrf();
      if (revision !== this.revision) return { kind: "superseded" };
      const finalProof = await this.managedProfileAt(revision);
      if (finalProof.kind !== "authenticated") return finalProof;
      if (finalProof.profile.id !== snapshot.actorId) return { kind: "superseded" };
      const response = await this.request("auth/sessions", {
        method: "DELETE",
        headers: { "content-type": "application/json", "x-csrftoken": csrf },
        body: JSON.stringify({ sessions: selected }),
      });
      if (revision !== this.revision) return { kind: "superseded" };
      if (response.status === 401) {
        const value = await json(response);
        if (
          !object(value) ||
          value.status !== 401 ||
          !object(value.meta) ||
          value.meta.is_authenticated !== false
        ) {
          throw new LibrarySessionProtocolError();
        }
        return { kind: "ended" };
      }
      if (response.status !== 200) return { kind: "unconfirmed", status: response.status };
      if (snapshot.sessions.some((row) => row.isCurrent && selected.includes(row.id))) {
        throw new LibrarySessionProtocolError();
      }
      const sessions = parseManagedSessions(await json(response));
      if (sessions.some((row) => selected.includes(row.id)))
        throw new LibrarySessionProtocolError();
      return { kind: "sessions", snapshot: { actorId: proof.profile.id, revision, sessions } };
    });
  }

  signOut(): Promise<LibrarySessionResult> {
    return this.transition((revision) => this.signOutAt(revision));
  }

  private async signOutAt(revision: number): Promise<LibrarySessionResult> {
    const csrf = await this.csrf();
    if (revision !== this.revision) return { kind: "superseded" };
    const response = await this.request("logout", {
      method: "POST",
      headers: { "x-csrftoken": csrf },
    });
    if (revision !== this.revision) return { kind: "superseded" };
    // No local cookie deletion or native-route fallback, including on 409.
    if (response.status === 200 || response.status === 401) return { kind: "ended" };
    return { kind: "unconfirmed", status: response.status };
  }
}

let sharedBrowserSession: LibraryBrowserSession | undefined;
export function getBrowserSession(): LibraryBrowserSession {
  sharedBrowserSession ??= new LibraryBrowserSession();
  return sharedBrowserSession;
}

// Keep incoming cross-tab boundaries separate from local transitions. Navigation
// is injected so tests exercise the same handler that the provider registers.
export function handleExternalBrowserSessionBoundary(
  event: Pick<StorageEvent, "key" | "newValue">,
  session: LibraryBrowserSession,
  location: Pick<Location, "pathname" | "reload" | "replace">,
): void {
  if (event.key !== BROWSER_SESSION_STORAGE_BOUNDARY || event.newValue === null) return;
  // Do not rebroadcast incoming signals. Invalidation still clears local
  // private caches and makes the old request's completion superseded.
  session.invalidate(false);
  if (location.pathname === "/logout" || location.pathname === "/logout/") {
    // Reload would remount auto-logout against replacement B's shared cookie.
    // Replace this action entry with a safe GET instead of creating a new end.
    location.replace("/login");
  } else {
    location.reload();
  }
}
